// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev

import HumanReadableTime from './HumanReadableTime';

export const RUN_ALL = 'all';
export const RUN_FULL = 'full';
export const RUN_HALF = 'half';
export const RUN_RELAY = 'relay';

export const RUNS = [RUN_FULL, RUN_HALF, RUN_RELAY];
export const RUNS_WITH_ALL = [RUN_ALL, RUN_FULL, RUN_HALF, RUN_RELAY];

export const WAYPOINT_TYPE_TIME = 'time';
export const WAYPOINT_TYPE_START = 'start';
export const WAYPOINT_TYPE_FINISH = 'finish';
export const WAYPOINT_TYPE_HANDOVER = 'handover';
export const WAYPOINT_TYPE_CHARITY = 'charity';

export const RUNS_HUMAN_READABLE = {
    [RUN_FULL]: 'Marathon',
    [RUN_HALF]: 'Halbmarathon',
    [RUN_RELAY]: 'Staffelmarathon',
};

export interface WaypointInterface {
    distance: number;
    position: google.maps.LatLng;
}

declare global {
    interface Window {
        config?: {
            paths?: Array<never>,
            themePath?: string,
            googleMapsApiKey?: string,
            environment?: string,
            language?: string,
        };
    }
}

export class Waypoint implements WaypointInterface {
    public position: google.maps.LatLng;

    public type: string;

    public distanceFull?: number;

    public distanceHalf?: number;

    public runs: string[];

    public run: string;

    public time: HumanReadableTime = new HumanReadableTime(0);

    public eta: HumanReadableTime = new HumanReadableTime(0);

    public deltaTime: HumanReadableTime = new HumanReadableTime(0);

    public passed: boolean = false;

    constructor(init?: Partial<Waypoint>) {
        Object.assign(this, init);
    }

    get distance(): number|undefined {
        if (this.run === RUN_HALF) {
            return this.distanceHalf;
        }

        return this.distanceFull;
    }

    static generateMarkerIconSmall(url: string): google.maps.Icon {
        return {
            url,
            scaledSize: new google.maps.Size(41, 45),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20.5, 22.5),
        };
    }

    static generateMarkerIconBig(url): google.maps.Icon {
        return {
            url,
            scaledSize: new google.maps.Size(73, 81),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(36.5, 40.5),
        };
    }

    getMarkerOptions(markerOptions: Partial<google.maps.MarkerOptions> = {}): google.maps.MarkerOptions {
        const defaultMarkerOptions = {
            icon: this.getMarkerIcon(),
            position: this.position,
            disableAutoPan: true,
        };

        return {...defaultMarkerOptions, ...markerOptions};
    }

    getMarkerIcon(): google.maps.Icon {
        const {themePath} = window.config;

        switch (this.type) {
            case WAYPOINT_TYPE_TIME:
                if (this.passed) {
                    return Waypoint.generateMarkerIconSmall(
                        `${themePath}/assets/images/marker/passed.png`,
                    );
                }
                return Waypoint.generateMarkerIconSmall(
                    `${themePath}/assets/images/marker/normal.png`,
                );
            case WAYPOINT_TYPE_START:
                return Waypoint.generateMarkerIconBig(
                    `${themePath}/assets/images/marker/start.png`,
                );
            case WAYPOINT_TYPE_FINISH:
                return Waypoint.generateMarkerIconBig(
                    `${themePath}/assets/images/marker/finish.png`,
                );
            case WAYPOINT_TYPE_HANDOVER:
                return Waypoint.generateMarkerIconSmall(
                    `${themePath}/assets/images/marker/handover.png`,
                );
            case WAYPOINT_TYPE_CHARITY:
                return Waypoint.generateMarkerIconSmall(
                    `${themePath}/assets/images/marker/charity.png`,
                );
            default:
                return null;
        }
    }

    isUsedForRun(run: string): boolean {
        return this.runs.includes(run);
    }
}

export function WAYPOINTS(): Array<Waypoint> {
    if (typeof google === 'undefined') return [];

    return [
        new Waypoint({
            distanceFull: 0,
            distanceHalf: 0,
            position: new google.maps.LatLng({
                lat: 50.939893,
                lng: 6.974166,
            }),
            runs: [RUN_ALL, RUN_FULL, RUN_HALF, RUN_RELAY],
            type: WAYPOINT_TYPE_START,
        }),
        new Waypoint({
            distanceFull: 5,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.906141,
                lng: 6.978339,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 10,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.911545,
                lng: 6.969737,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 11.7,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.922122,
                lng: 6.959407,
            }),
            runs: [RUN_RELAY],
            type: WAYPOINT_TYPE_HANDOVER,
        }),
        new Waypoint({
            distanceFull: 15,
            distanceHalf: 3.6,
            position: new google.maps.LatLng({
                lat: 50.933064,
                lng: 6.939784,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 21.0975,
            distanceHalf: 9.6,
            position: new google.maps.LatLng({
                lat: 50.932285,
                lng: 6.920841,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: -1,
            distanceHalf: 10,
            position: new google.maps.LatLng({
                lat: 50.930537,
                lng: 6.914859,
            }),
            runs: [RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 23,
            distanceHalf: 11.5,
            position: new google.maps.LatLng({
                lat: 50.936531,
                lng: 6.927637,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_CHARITY,
        }),
        new Waypoint({
            distanceFull: 23.4,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.936189,
                lng: 6.929579,
            }),
            runs: [RUN_RELAY],
            type: WAYPOINT_TYPE_HANDOVER,
        }),
        new Waypoint({
            distanceFull: 25,
            distanceHalf: 13.5,
            position: new google.maps.LatLng({
                lat: 50.941681,
                lng: 6.936263,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 29.8,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.950014,
                lng: 6.955039,
            }),
            runs: [RUN_RELAY],
            type: WAYPOINT_TYPE_HANDOVER,
        }),
        new Waypoint({
            distanceFull: 30,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.952482,
                lng: 6.961067,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 35,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.967439,
                lng: 6.960387,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 40,
            distanceHalf: 18.9,
            position: new google.maps.LatLng({
                lat: 50.936546,
                lng: 6.939101,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 42.195,
            distanceHalf: 21.0975,
            position: new google.maps.LatLng({
                lat: 50.941583,
                lng: 6.953627,
            }),
            runs: [RUN_ALL, RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_FINISH,
        }),
    ];
}

export function findWaypointForPosition(waypoints: Waypoint[], position: google.maps.LatLng): Waypoint | null {
    if (!waypoints) return null;
    return waypoints.find((waypoint) => waypoint.position.equals(position)) || null;
}

export function getWaypointsForRun(run: string): Array<Waypoint> {
    // Filter for waypoints used for this run
    const waypointsForRun = WAYPOINTS().filter((waypoint) => waypoint.runs.indexOf(run) !== -1);

    // Set Waypoint.run
    return waypointsForRun.map((waypoint) => new Waypoint({...waypoint, run, runs: [run]}));
}

export function getWaypointDistancesForRun(run): Array<number> {
    return getWaypointsForRun(run)
        .filter((waypoint) => waypoint.type !== WAYPOINT_TYPE_CHARITY)
        .map((waypoint) => waypoint.distance)
        .slice(1);
}
